import { useState } from 'react';
import { HistoryModal } from '@/components/history/HistoryModal';
import type { HistoryColumn, HistoryTab } from '@/components/history/types';
import { RecordLink } from '@/components/links/RecordLinks';
import { useMemberTransactionsQuery } from '@/api/query/user';
import type { MemberTransactionKind, MemberTransactionType } from '@/api/type/transaction';
import type { Member, MemberTransaction } from '@/types';
import { memberHeading } from '@/lib/utils';
import { formatCurrency, formatPaymentDate, paymentTypeLabel } from '@/lib/payments';

const TYPE_LABEL: Record<MemberTransactionType, string> = {
  policy_payment: 'Policy Payment',
  loan_given: 'Loan Given',
  loan_repayment: 'Loan Repayment',
  balance_added: 'Paid by PMS',
  balance_repaid: 'Balance Repaid',
};

const TABS: { id: 'all' | MemberTransactionKind; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'policy', label: 'Policy' },
  { id: 'loan', label: 'Loan' },
  { id: 'balance', label: 'Balance' },
];

const COLUMNS: HistoryColumn[] = [
  { label: 'Date' },
  { label: 'Type' },
  { label: 'Policy / Loan' },
  { label: 'Details' },
  { label: 'Mode' },
  { label: 'Amount', align: 'right' },
];

interface MemberTransactionsModalProps {
  member: Member | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Full-height popup with every policy, loan and balance transaction of one member. */
export function MemberTransactionsModal({ member, open, onOpenChange }: MemberTransactionsModalProps) {
  const [activeTab, setActiveTab] = useState<string>('all');
  const { data: transactions = [], isLoading, isError } = useMemberTransactionsQuery(member?.id, open);

  const toRows = (items: MemberTransaction[]) =>
    items.map((item) => ({
      key: item.id,
      cells: [
        formatPaymentDate(item.date),
        <span key="type" className="font-medium text-gray-900">{TYPE_LABEL[item.type] ?? item.type}</span>,
        member ? <RecordLink key="ref" member={member} refNo={item.loanNo ?? item.policyNo} /> : '--',
        <span key="note" className="text-gray-500">{item.note ?? '--'}</span>,
        <span key="mode" className="text-gray-900">{paymentTypeLabel(item.paymentType) || '--'}</span>,
        <b key="amount">{formatCurrency(item.amount)}</b>,
      ],
    }));

  const tabs: HistoryTab[] = TABS.map((tab) => ({
    id: tab.id,
    label: tab.label,
    columns: COLUMNS,
    rows: toRows(tab.id === 'all' ? transactions : transactions.filter((item) => item.kind === tab.id)),
    empty: isError ? 'Unable to load transactions. Please try again.' : 'No transactions yet.',
  }));

  return (
    <HistoryModal
      open={open}
      onOpenChange={(next) => {
        if (!next) setActiveTab('all');
        onOpenChange(next);
      }}
      title="Transaction History"
      subtitle={member && `${memberHeading(member)} · Policy, loan and balance transactions`}
      tabs={tabs}
      activeId={activeTab}
      onTabChange={setActiveTab}
      loading={isLoading}
    />
  );
}
